import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Pusher from "pusher-js";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { BtnComponent } from "../../components/button";
import { organizationConfig } from "../../services/login";
import {
  fetchGuestTelemedLocations,
  fetchGuestTelemedProviders,
  requestGuestTelemedSession,
} from "../../services/guestVezoApi";
import { getBaseUrl, setBaseUrl } from "../../helpers/utility";
import {
  guestLocationId,
  guestProviderRecordId,
} from "./guestVezoUtils";
import FullScreenLoader from "../../components/FullScreenLoader";
import GuestLocationStep from "./steps/GuestLocationStep";
import GuestProvidersPanel from "./steps/GuestProvidersPanel";

const TELEMED_OPEN_TYPES = [
  "telemed_call_started",
  "telemed_user_added_to_room",
  "telemed_visitor_added_data",
  "telemed_call_joined",
  "telemed_visit_created"
];

const TELEMED_CLOSE_TYPES = ["telemed_call_ended", "telemed_call_finished", "telemed_call_left", "telemed_call_cancelled"];

/** Stable random id for guest Pusher channel; prefix required by product. */
function createGuestPusherChannelName() {
  const part =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID().replace(/-/g, "")
      : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 14)}`;
  return `vezosession-${part}`;
}

function GuestVezoRequest() {
  const [searchParams] = useSearchParams();

  /** Organization / tenant code from landing link (?org= or ?org_code=) */
  const orgFromUrl = useMemo(() => {
    const o = searchParams.get("org") ?? searchParams.get("org_code");
    return o ? o.trim() : "";
  }, [searchParams]);

  const practiceKeyFromUrl = useMemo(() => {
    const p = searchParams.get("practice_key") ?? searchParams.get("practiceKey");
    return p ? p.trim() : "";
  }, [searchParams]);

  const [step, setStep] = useState(1);
  const [locations, setLocations] = useState([]);
  const [practiceNameFromApi, setPracticeNameFromApi] = useState("");
  const [locationProviders, setLocationProviders] = useState([]);
  /** Full provider payload for current location (no keyword); restores list when search has fewer than 3 characters */
  const [cachedLocationProviders, setCachedLocationProvider] = useState([]);
  const [providersInitialDone, setProvidersInitialDone] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [locationPick, setLocationPick] = useState("");
  const [providerSearch, setProviderSearch] = useState("");

  const [locationsLoading, setLocationsLoading] = useState(false);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [sessionSubmitting, setSessionSubmitting] = useState(false);

  const [guestCallUrl, setGuestCallUrl] = useState(null);
  const [pusherChannelName, setPusherChannelName] = useState(() => createGuestPusherChannelName());
  const [orgConfigReady, setOrgConfigReady] = useState(false);

  const providerSearchSeqRef = useRef(0);

  const baseUrlSettings = useMemo(() => getBaseUrl(), [orgFromUrl, orgConfigReady]);

  useEffect(() => {
    let cancelled = false;
    setOrgConfigReady(false);
    (async () => {
      try {
        const res = await organizationConfig();
        if (cancelled) return;
        if (res?.data?.response?.code == 0) {
          localStorage.setItem("organization_config", JSON.stringify(res?.data?.response?.data));
        }
      } catch {
        /* optional bootstrap */
      }
      if (cancelled) return;
      if (orgFromUrl) {
        try {
          setBaseUrl(orgFromUrl);
        } catch {
          /* ignore */
        }
      }
      setOrgConfigReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [orgFromUrl]);

  const loadLocations = useCallback(async () => {
    setLocationsLoading(true);
    try {
      const res = await fetchGuestTelemedLocations({
        practice_key: practiceKeyFromUrl,
      });
      
      setPracticeNameFromApi(res[0].practice_name || "");
      setLocations(res[0].locations);
      if (!res[0].locations.length) {
        toast.info("No locations returned.");
      }
      return res[0].locations;
    } catch (e) {
      toast.error(e?.message || "Failed to load locations");
      setLocations([]);
      setPracticeNameFromApi("");
      return [];
    } finally {
      setLocationsLoading(false);
    }
  }, [orgFromUrl, practiceKeyFromUrl]);

  useEffect(() => {
    if (!orgConfigReady || step !== 1) return;
    loadLocations();
  }, [step, loadLocations, orgConfigReady]);

  useEffect(() => {
    providerSearchSeqRef.current += 1;
    const lid = guestLocationId(selectedLocation);
    if (!lid) {
      setLocationProviders([]);
      setCachedLocationProvider([]);
      setSelectedProvider(null);
      setProvidersInitialDone(false);
      return;
    }

    let cancelled = false;
    setProvidersInitialDone(false);
    (async () => {
      setProvidersLoading(true);
      setSelectedProvider(null);
      setLocationProviders([]);
      setCachedLocationProvider([]);
      try {
        const list = await fetchGuestTelemedProviders({ practice_key: practiceKeyFromUrl, location_key: lid });
        if (cancelled) return;
        const arr = Array.isArray(list) ? list : [];
        setCachedLocationProvider(arr);
        setLocationProviders(arr);
        if (!arr.length) {
          toast.info("No providers for this location.");
        }
      } catch (e) {
        if (!cancelled) {
          toast.error(e?.message || "Failed to load providers");
          setLocationProviders([]);
          setCachedLocationProvider([]);
        }
      } finally {
        if (!cancelled) {
          setProvidersLoading(false);
          setProvidersInitialDone(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedLocation]);

  /** Server search when keyword length ≥ 3; otherwise show cached full list */
  useEffect(() => {
    const lid = guestLocationId(selectedLocation);
    if (!lid || !providersInitialDone) return;

    const q = providerSearch.trim();
    if (q.length < 3) {
      providerSearchSeqRef.current += 1;
      setLocationProviders(cachedLocationProviders);
      return;
    }

    const seq = ++providerSearchSeqRef.current;
    const t = window.setTimeout(async () => {
      setProvidersLoading(true);
      try {
        const list = await fetchGuestTelemedProviders({ location_id: lid, keyword: q });
        if (seq !== providerSearchSeqRef.current) return;
        setLocationProviders(Array.isArray(list) ? list : []);
      } catch (e) {
        if (seq !== providerSearchSeqRef.current) return;
        toast.error(e?.message || "Provider search failed");
        setLocationProviders(cachedLocationProviders);
      } finally {
        if (seq === providerSearchSeqRef.current) {
          setProvidersLoading(false);
        }
      }
    }, 320);

    return () => window.clearTimeout(t);
  }, [providerSearch, selectedLocation, providersInitialDone, cachedLocationProviders]);

  const handleLocationFieldChange = (location) => {
    const idStr = guestLocationId(location);
    setLocationPick(idStr);
    setProviderSearch("");
    if (!idStr) {
      setSelectedLocation(null);
      setLocationProviders([]);
      setCachedLocationProvider([]);
      setProvidersInitialDone(false);
      setSelectedProvider(null);
      return;
    }
    setSelectedLocation(location);
  };

  const submitSessionRequest = async () => {
    const providerId = guestProviderRecordId(selectedProvider);
    const locId = guestLocationId(selectedLocation);
    if (!locId || providerId == null || providerId === "") {
      toast.error("Select a location and provider");
      return;
    }
    setSessionSubmitting(true);
    try {
      const data = await requestGuestTelemedSession({
        practice_key: practiceKeyFromUrl,
        location_key: locId,
        provider_key: providerId,
        pusher_key: pusherChannelName,
      });
      console.log("Response data:", data);
      if (data.visit_url) {
        setGuestCallUrl(data.visit_url);
      } else if (data.visit_key && getBaseUrl().telemed_url) {
        const pid = data.practice_id ?? selectedLocation.practice_id ?? getBaseUrl()?.practice_id;
        const token = data.guest_token ?? data.token ?? "";
        setGuestCallUrl(
          `${getBaseUrl().telemed_url}?mid=${encodeURIComponent(data.visit_key)}&token=${encodeURIComponent(token)}&parent=guest_visitor&tz=${new Date().getTimezoneOffset()}${pid ? `&practice_id=${pid}` : ""}&consent_signed=1`
        );
      }
      setStep(3);
      toast.success("Request sent. When the provider is ready, the video window will open.");
    } catch (e) {
      toast.error(e?.message || "Failed to request session");
    } finally {
      setSessionSubmitting(false);
    }
  };

  useEffect(() => {
    if (!orgConfigReady || !pusherChannelName?.trim() || !baseUrlSettings?.pusher_settings?.key) {
      return undefined;
    }

    const pusher = new Pusher(baseUrlSettings.pusher_settings.key, {
      cluster: baseUrlSettings.pusher_settings.cluster,
    });

    const channel = pusher.subscribe(pusherChannelName);
    console.log("channel:", channel);
    const onMyEvent = (data) => {
      console.log("Pusher data:", data);
      if (TELEMED_CLOSE_TYPES.includes(data?.msgtype)) {
        setGuestCallUrl(null);
        return;
      }
      if (TELEMED_OPEN_TYPES.includes(data?.msgtype) || data?.vezo_url) {
        if (data.vezo_url) {
          window.location.assign(String(data.vezo_url));
          // setGuestCallUrl(data.visit_url);
        } else if (data.visit_key && getBaseUrl().telemed_url) {
          setGuestCallUrl(
            `${getBaseUrl().telemed_url}?mid=${encodeURIComponent(data.visit_key)}&tz=${new Date().getTimezoneOffset()}`
          );
        }
      }
    };

    channel.bind("my_event", onMyEvent);

    return () => {
      try {
        channel.unbind("my_event", onMyEvent);
        pusher.unsubscribe(pusherChannelName);
        pusher.disconnect();
      } catch {
        /* ignore */
      }
    };
  }, [pusherChannelName, orgConfigReady, baseUrlSettings?.pusher_settings?.key, baseUrlSettings?.pusher_settings?.cluster]);

  const btnFullMobile = "w-full min-h-[44px] justify-center sm:w-auto sm:min-h-0 sm:min-w-[200px]";

  const selectedLocationIdStr = guestLocationId(selectedLocation);

  const hasLocation = Boolean(selectedLocationIdStr);

  const canRequest =
    hasLocation &&
    selectedProvider &&
    guestProviderRecordId(selectedProvider) != null &&
    guestProviderRecordId(selectedProvider) !== "";

  const startOver = () => {
    setStep(1);
    setLocationPick("");
    setSelectedLocation(null);
    setLocationProviders([]);
    setCachedLocationProvider([]);
    setProvidersInitialDone(false);
    setSelectedProvider(null);
    setProviderSearch("");
    setPusherChannelName(createGuestPusherChannelName());
    setGuestCallUrl(null);
    loadLocations();
  };

  return (
    <div
      className={`bg-slate-50 text-gray-900 ${
        step === 1
          ? "flex h-[100dvh] max-h-[100dvh] flex-col overflow-hidden overscroll-none"
          : "min-h-[100dvh]"
      }`}
    >
      {!orgConfigReady ? <FullScreenLoader /> : null}
      <div
        className={`mx-auto flex w-full max-w-6xl flex-col px-4 pt-[max(0.75rem,env(safe-area-inset-top))] sm:px-6 sm:pt-6 ${
          step === 1 ? "min-h-0 flex-1 pb-0" : "pb-[max(1rem,env(safe-area-inset-bottom))] sm:pb-8"
        }`}
        style={{ WebkitTapHighlightColor: "transparent" }}
      >
        <header className="mb-3 shrink-0 text-center sm:mb-4">
          <h1 className="text-xl font-semibold tracking-tight text-slate-800 sm:text-2xl">Vezo Session (guest)</h1>
          {step !== 3 ? (
            <p className="mx-auto mt-1.5 max-w-2xl text-sm leading-relaxed text-slate-600 sm:text-[15px]">
              Choose a location, pick a provider, then send your session request.
            </p>
          ) : null}
        </header>

        {step === 3 ? (
          <div className="mx-auto w-full max-w-[min(100%,26rem)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg shadow-slate-900/5 sm:max-w-md">
            <div className="border-b border-slate-100 bg-slate-50/90 px-4 py-3 sm:px-5 sm:py-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-teal-700">Almost there</p>
              <h2 className="mt-1 text-base font-semibold text-slate-900 sm:text-lg">Waiting for provider</h2>
            </div>
            <div className="px-4 py-5 sm:px-5 sm:py-6">
              <p className="text-sm leading-relaxed text-slate-600">
                Keep this page open. When the provider starts the visit, the video opens automatically.
              </p>
            </div>
          </div>
        ) : (
          <article className="mx-auto flex min-h-0 w-full max-w-[min(100%,26rem)] flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg shadow-slate-900/5 sm:max-w-lg">

            <ol
              className="flex min-h-0 flex-1 list-none flex-col divide-y divide-slate-100 overflow-hidden p-0"
              aria-label="Visit booking steps"
            >
              <li className="shrink-0 px-3 py-3 sm:px-4 sm:py-3.5">
                <div className="flex gap-3 sm:gap-4">
                  <div className="min-w-0 flex-1">
                    <GuestLocationStep
                      practiceName={practiceNameFromApi}
                      locations={locations}
                      locationPick={locationPick}
                      locationsLoading={locationsLoading}
                      onLocationChange={handleLocationFieldChange}
                      className="w-full"
                    />
                  </div>
                </div>
              </li>
              <li className="flex min-h-0 min-w-0 flex-1 flex-col px-3 py-3 sm:px-4 sm:py-3.5">
                <GuestProvidersPanel
                  displayRows={locationProviders}
                  cachedProviderRowCount={cachedLocationProviders.length}
                  providersInitialDone={providersInitialDone}
                  providersLoading={providersLoading}
                  hasLocation={hasLocation}
                  providerSearch={providerSearch}
                  onProviderSearchChange={setProviderSearch}
                  selectedProvider={selectedProvider}
                  onSelectProvider={setSelectedProvider}
                  className="flex min-h-0 min-w-0 w-full flex-1 flex-col"
                />
              </li>
            </ol>

            {selectedProvider ? (
              <div className="shrink-0 border-t border-slate-100 bg-slate-50/80 px-3 py-2.5 text-center sm:px-4">
                <p className="text-[11px] text-slate-500">Selected provider</p>
                <p className="mt-0.5 text-sm font-semibold text-slate-900">{selectedProvider?.name}</p>
              </div>
            ) : null}
          </article>
        )}

        {step === 1 ? (
          <footer className="mx-auto mt-2 w-full max-w-[min(100%,26rem)] shrink-0 rounded-2xl border border-slate-200 bg-white pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-2 shadow-lg shadow-slate-900/5 sm:mb-[max(0.25rem,env(safe-area-inset-bottom))] sm:max-w-lg">
            <div className="flex w-full flex-col gap-1.5 px-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-4 sm:py-3">
              <div className="hidden min-w-0 sm:block sm:max-w-[13rem] sm:text-left">
                <p className="text-[11px] leading-snug text-slate-500">
                  Select a location and provider to request a Vezo Session.
                </p>
              </div>
              <div className="flex w-full flex-col items-stretch gap-1 sm:w-auto sm:items-end">
                <BtnComponent
                  type="button"
                  btnType="primary"
                  text="Request Vezo Session"
                  className={`${btnFullMobile} !min-h-[44px] rounded-lg !border-teal-700 !bg-teal-700 !px-5 !text-sm !font-semibold shadow-sm transition hover:!border-teal-800 hover:!bg-teal-800 sm:!min-w-[200px]`}
                  onClick={submitSessionRequest}
                  disabled={sessionSubmitting || !canRequest}
                  loading={sessionSubmitting}
                />
              </div>
            </div>
          </footer>
        ) : null}
      </div>

      {guestCallUrl ? (
        <div
          className="fixed inset-0 z-[2000] flex flex-col bg-black/60 sm:items-center sm:justify-center sm:bg-black/50 sm:p-4"
          style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden bg-white sm:max-h-[min(90dvh,880px)] sm:max-w-4xl sm:flex-none sm:rounded-2xl sm:shadow-2xl">
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 px-4 py-3 sm:py-2.5">
              <span className="text-sm font-medium text-slate-800">Vezo Session</span>
              <button
                type="button"
                className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg px-3 text-sm font-medium text-red-600 hover:bg-red-50 active:bg-red-100 sm:min-h-0 sm:min-w-0 sm:px-2 sm:py-1"
                onClick={() => setGuestCallUrl(null)}
              >
                Close
              </button>
            </div>
            <iframe
              title="Guest telemed"
              src={guestCallUrl}
              className="min-h-0 w-full flex-1 border-0 sm:min-h-[min(70vh,560px)]"
              allow="microphone; camera; autoplay; speaker; display-capture"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default GuestVezoRequest;
